import { GcpRequestData, GcpRequestUploadFile, useUploadStore } from '../../../../../../store/uploadStore';
import { FileWithPath } from 'react-dropzone';
import getPilotApiService, { FileUploadContext } from '../../../../../../api_services/PilotApiService';
import { isCancelledByUserError } from '../../../../../../errors/createCanceledError';
import * as Sentry from '@sentry/browser';
import { Customer, Site } from '../../../../../../types';
import { GcpRequestFileType } from '../gcpRequestFileType';
import { trackAction } from '../../../../../../libs/analytics/ActionTracking';
import { AxiosProgressEvent } from 'axios';
import { calcHash } from '../../../../../../libs/file';
import {
  calcGcpCountFromResults,
  calcGcpHoursFromResults,
  getGcpMeasurementDates,
  PREP_MS,
  isGcpParseableFile,
  parseGcpCsvFile,
} from './parseGcpCsv';

export const getGcpRequestMeasurementDates = (gcpRequest: GcpRequestData) =>
  getGcpMeasurementDates([
    ...(gcpRequest.restoredMeasurementParseResults ?? []),
    ...gcpRequest.measurementUploads.filter((u) => u.isDone).map((u) => u.parseResult),
  ]);

export const recalcGcpStats = (gcpRequest: GcpRequestData) => {
  gcpRequest.meta.areServiceHoursConfirmed = false;
  const doneUploads = gcpRequest.measurementUploads.filter((u) => u.isDone);
  const parseResults = doneUploads.map((u) => u.parseResult);
  // GCP count covers both files uploaded this session and files already saved on the backend
  // when this request was restored; hours below intentionally only use this session's uploads.
  gcpRequest.gcpCount = calcGcpCountFromResults([
    ...(gcpRequest.restoredMeasurementParseResults ?? []),
    ...parseResults,
  ]);

  const rawNewHours = calcGcpHoursFromResults(parseResults);
  if (rawNewHours === undefined) return;

  // Files in measurements that weren't uploaded in this session are backend files whose
  // hours were already accounted for in prevGcpServiceLaborTimeInHours.
  const uploadedUrls = new Set(doneUploads.map((u) => u.uploadedUrl).filter(Boolean));
  const backendHours = gcpRequest.measurements.some((url) => !uploadedUrls.has(url))
    ? gcpRequest.meta.prevGcpServiceLaborTimeInHours ?? 0
    : 0;

  const prepHours = PREP_MS / (1000 * 60 * 60);
  const autoHours = Math.ceil((rawNewHours + prepHours + backendHours) / 0.25) * 0.25;
  gcpRequest.autoGcpServiceLaborTimeInHours = autoHours;
  gcpRequest.gcpServiceLaborTimeInHours = autoHours; // reset in UI to autoHours
};

const filterOutAlreadySelectedFiles = (files: FileWithPath[], uploads: GcpRequestUploadFile[]) => {
  const alreadySelectedHashes = uploads.map(({ file }) => calcHash(file));

  return files.filter((file) => !alreadySelectedHashes.includes(calcHash(file)));
};

export const useHandleGcpFileUpload = (
  customer: Customer,
  site: Site,
  gcpRequest: GcpRequestData,
  type: GcpRequestFileType,
) => {
  const { setGcpRequest } = useUploadStore();

  const isMeasurement = type === GcpRequestFileType.Measurement;

  return (selectedFiles: FileWithPath[]) => {
    const uploads = isMeasurement ? gcpRequest.measurementUploads : gcpRequest.photoUploads;
    const files = isMeasurement ? gcpRequest.measurements : gcpRequest.photos;

    const filteredFiles = filterOutAlreadySelectedFiles(selectedFiles, uploads);

    if (!filteredFiles.length) return;

    if (type === GcpRequestFileType.Photo) {
      trackAction('Add GCP photos', { customer, site });
    } else {
      trackAction('Add GCP measurements', { customer, site });
    }

    for (const file of filteredFiles) {
      const abortController = new AbortController();

      (async () => {
        const upload: GcpRequestUploadFile = {
          file,
          uploadedSize: 0,
          abortController,
          isDone: false,
          promise: getPilotApiService().uploadPilotAttachment(
            file,
            isMeasurement ? FileUploadContext.FlightMarkersCoordinates : FileUploadContext.FlightMarkersImages,
            abortController.signal,
            (progressEvent: AxiosProgressEvent) => {
              upload.uploadedSize = Math.min(progressEvent.loaded, file.size);
              setGcpRequest(customer.id, site.id, gcpRequest);
            },
          ),
        };

        uploads.push(upload);
        setGcpRequest(customer.id, site.id, gcpRequest);

        try {
          const path = await upload.promise;

          files.push(path);
          upload.isDone = true;
          upload.uploadedUrl = path;

          if (isMeasurement) {
            upload.parseResult = isGcpParseableFile(file.name) ? await parseGcpCsvFile(file) : null;
            if (upload.parseResult) recalcGcpStats(gcpRequest);
          }

          setGcpRequest(customer.id, site.id, gcpRequest);
        } catch (e: unknown) {
          if (isCancelledByUserError(e)) return null;

          alert(`Couldn't upload GCP Request file for ${customer.name} ${site.name}`);
          Sentry.captureException(e);
        }
      })();
    }
  };
};
